import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Bell, Inbox, MessageSquare, FileCheck2, Paperclip, Receipt, RefreshCw, ArrowRight, ChevronDown, Reply } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtDateTime, errMsg } from "../../lib/api";
import { COMMERCIAL, OFFER_LABELS, PAYMENT, BAT, PRODUCTION } from "../../lib/labels";
import { StatusBadge, useStaff } from "./AdminShell";
import { AstraDraft } from "./Astra";
import { InviteClient } from "./InviteClient";
import { useFeed } from "./useFeed";

const KIND = {
  nouvelle_demande: ["Nouvelle demande", Inbox, "#3B6EA8"], message_client: ["Réponse client", MessageSquare, "#C25E38"], bat: ["BAT", FileCheck2, "#6B5B95"],
  fichier: ["Fichier", Paperclip, "#8A6D1F"], devis: ["Devis", Receipt, "#2F7D5B"], message_atelier: ["Atelier", MessageSquare, "#8C857B"], statut: ["Statut", RefreshCw, "#8C857B"], client: ["Client", Bell, "#C25E38"], atelier: ["Atelier", RefreshCw, "#8C857B"],
};

const ALL_LABELS = { ...PRODUCTION, ...BAT, ...PAYMENT, ...COMMERCIAL };
const humanize = (s = "") => s.replace(/[a-z_]+/g, (w) => ALL_LABELS[w] || w);

function dayLabel(iso) {
  const d = new Date(iso), t = new Date(); const y = new Date(); y.setDate(t.getDate() - 1);
  if (d.toDateString() === t.toDateString()) return "Aujourd'hui";
  if (d.toDateString() === y.toDateString()) return "Hier";
  return fmtDate(iso);
}

function QuickReply({ item, onDone }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try { const r = await api.post(`/admin/requests/${item.request_id}/messages`, { body, internal: false }); toast.success(r.data.email_sent === false ? "Réponse enregistrée (email non envoyé)" : `Réponse envoyée à ${item.who}`); setBody(""); setOpen(false); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!open) return <button className="k-link text-xs flex items-center gap-1" onClick={() => setOpen(true)} data-testid={`feed-reply-${item.id}`}><Reply size={12} /> Répondre</button>;
  return (
    <div className="mt-2 grid gap-2" data-testid={`feed-reply-box-${item.id}`}>
      <textarea className="k-input text-sm py-2" rows={2} autoFocus value={body} onChange={(e) => setBody(e.target.value)} placeholder={`Réponse à ${item.who} (visible dans son espace, notifiée par email)`} data-testid={`feed-reply-input-${item.id}`} />
      <div className="flex gap-2 flex-wrap items-center"><button className="k-btn k-btn-sm" disabled={busy || !body.trim()} onClick={send} data-testid={`feed-reply-send-${item.id}`}>Envoyer</button><button className="k-btn-outline k-btn-sm" onClick={() => setOpen(false)}>Annuler</button><AstraDraft rid={item.request_id} kind="reply" onText={setBody} /></div>
    </div>
  );
}

function Feed() {
  const { items, unread, lastAt, seenAt, refresh, markSeen, askPermission } = useFeed();
  const { user } = useStaff();
  const [filter, setFilter] = useState("tout");
  useEffect(() => { askPermission(); const t = setTimeout(markSeen, 2500); return () => clearTimeout(t); }, [markSeen, askPermission, items.length]);
  const rows = items.filter((i) => filter === "tout" || (filter === "client" ? i.actor === "client" : i.actor !== "client"));
  let lastDay = "";
  return (
    <section className="k-card p-0 overflow-hidden" data-testid="dashboard-feed">
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#ECE6DC] flex-wrap">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-2xl">Fil d'actualité</h2>
          {unread > 0 && <span className="k-badge text-[#C25E38] border-[#E5A992]" data-testid="feed-unread">{unread} nouveau{unread > 1 ? "x" : ""}</span>}
        </div>
        <div className="flex items-center gap-2 text-xs">
          {[["tout", "Tout"], ["client", "Clients"], ["atelier", "Atelier"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full border transition-colors ${filter === k ? "bg-[#1C1917] text-white border-[#1C1917]" : "border-[#E8E2D8] hover:border-[#1C1917]"}`} data-testid={`feed-filter-${k}`}>{l}</button>
          ))}
          <button onClick={refresh} className="p-1.5 text-[#8C857B] hover:text-[#1C1917]" aria-label="Actualiser" data-testid="feed-refresh"><RefreshCw size={14} /></button>
        </div>
      </header>
      <ol className="divide-y divide-[#F1ECE4] max-h-[70vh] overflow-auto" data-testid="feed-list">
        {rows.map((i) => {
          const [label, Icon, color] = KIND[i.kind] || KIND.atelier;
          const day = dayLabel(i.at); const showDay = day !== lastDay; lastDay = day;
          const isNew = i.actor === "client" && i.at > seenAt;
          return (
            <li key={i.id} data-testid={`feed-item-${i.id}`}>
              {showDay && <p className="k-eyebrow text-[#8C857B] px-5 pt-4 pb-1">{day}</p>}
              <div className={`grid grid-cols-[2rem_minmax(0,1fr)_auto] gap-3 px-5 py-3 items-start hover:bg-[#FBF8F3] transition-colors ${i.actor === "client" ? "bg-[#FFFDFA]" : ""}`}>
                <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: color + "1A", color }}><Icon size={15} /></span>
                <span className="min-w-0">
                  <Link to={`/admin/dossiers/${i.request_id}`} className="text-sm block hover:text-[#C25E38]"><strong className="font-medium">{i.who}</strong> <span className="text-[#57534E]">— {i.action}</span>{isNew && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-[#C25E38] align-middle" />}</Link>
                  {i.detail && <span className="text-xs text-[#8C857B] block truncate">{i.kind === "statut" ? humanize(i.detail) : i.detail}</span>}
                  <span className="text-[11px] text-[#8C857B] font-mono-k block">{i.ref} · {label} · {OFFER_LABELS[i.offer]}</span>
                  {i.actor === "client" && user.role !== "production" && <span className="block mt-1"><QuickReply item={i} onDone={refresh} /></span>}
                </span>
                <span className="text-xs text-[#8C857B] whitespace-nowrap">{new Date(i.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            </li>
          );
        })}
        {!rows.length && <li className="px-5 py-8 text-sm text-[#8C857B]">Aucune activité pour le moment.</li>}
      </ol>
      <footer className="px-5 py-2 text-[11px] text-[#8C857B] border-t border-[#ECE6DC]">Mise à jour automatique toutes les 30 s{lastAt ? ` · dernière à ${new Date(lastAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : ""}</footer>
    </section>
  );
}

function Todo({ d }) {
  const rows = [
    ...d.to_process.map((r) => ({ r, why: "Nouvelle demande à étudier", tone: "#3B6EA8", p: 0 })),
    ...d.bat_corrections.map((r) => ({ r, why: "Corrections BAT demandées", tone: "#C25E38", p: 1 })),
    ...d.to_remind.map((r) => ({ r, why: r.reminder_kind === "devis" ? "Devis sans réponse — relancer" : "Complément sans réponse — relancer", tone: "#8A6D1F", p: 2 })),
    ...d.deadlines.map((r) => ({ r, why: `${r.next_action || "Action"} — ${fmtDate(r.next_action_due)}`, tone: "#6B5B95", p: 3 })),
    ...d.incomplete.map((r) => ({ r, why: `En attente du client${(r.missing_info || []).length ? ` : ${r.missing_info.join(", ")}` : ""}`, tone: "#8C857B", p: 4 })),
  ];
  const seen = new Set(); const uniq = rows.filter(({ r }) => !seen.has(r.id) && seen.add(r.id)).sort((a, b) => a.p - b.p);
  const [all, setAll] = useState(false);
  const shown = all ? uniq : uniq.slice(0, 6);
  return (
    <section className="k-card p-5 min-w-0" data-testid="dashboard-todo">
      <div className="flex items-baseline justify-between"><h2 className="font-display text-2xl">À traiter maintenant</h2><span className="font-mono-k text-xs text-[#8C857B]">{uniq.length}</span></div>
      <ul className="mt-3 divide-y divide-[#F1ECE4]">
        {shown.map(({ r, why, tone }) => (
          <li key={r.id}>
            <Link to={`/admin/dossiers/${r.id}`} className="py-3 flex items-center gap-3 text-sm hover:text-[#C25E38] group min-w-0" data-testid={`todo-${r.id}`}>
              <span className="w-1.5 h-8 rounded-full shrink-0" style={{ background: tone }} />
              <span className="min-w-0 flex-1"><span className="block truncate"><strong className="font-medium">{r.contact?.company || r.contact?.name}</strong> <span className="font-mono-k text-[11px] text-[#8C857B]">{r.ref}</span></span><span className="block text-xs text-[#57534E] truncate">{why}</span></span>
              <StatusBadge value={r.status_commercial} /><ArrowRight size={14} className="text-[#D6CEBF] group-hover:text-[#C25E38]" />
            </Link>
          </li>
        ))}
        {!uniq.length && <li className="py-6 text-sm text-[#8C857B]">Tout est à jour. Rien à traiter.</li>}
      </ul>
      {uniq.length > 6 && <button className="k-link text-xs mt-2 flex items-center gap-1" onClick={() => setAll(!all)} data-testid="todo-toggle">{all ? "Réduire" : `Voir les ${uniq.length - 6} autres`} <ChevronDown size={12} className={all ? "rotate-180" : ""} /></button>}
    </section>
  );
}

function Unpaid() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/admin/pennylane/unpaid").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, []);
  if (err || !d || d.configured === false) return null;
  const total = d.items.reduce((s, i) => s + (Number(i.remaining ?? i.amount_ttc) || 0), 0);
  const overdue = d.items.filter((i) => i.overdue);
  return (
    <details className="k-card p-5" data-testid="dashboard-unpaid">
      <summary className="cursor-pointer flex items-center justify-between gap-3 list-none">
        <span><span className="k-eyebrow text-[#57534E]">Encaissements</span><span className="block font-display text-2xl mt-1">{total.toFixed(0)} € <span className="text-sm text-[#57534E] font-sans">TTC à encaisser</span></span></span>
        <span className="text-right text-xs text-[#57534E]">{d.items.length} facture{d.items.length > 1 ? "s" : ""}{overdue.length > 0 && <span className="block text-[#A84C2A] font-medium">{overdue.length} en retard</span>}</span>
      </summary>
      <ul className="mt-4 divide-y divide-[#F1ECE4] text-sm">
        {d.items.slice(0, 10).map((i) => (
          <li key={i.id} className="py-2 flex justify-between gap-3" data-testid={`unpaid-${i.id}`}>
            <span className="min-w-0 truncate"><span className="font-mono-k text-xs">{i.number}</span> · {i.customer}<span className="block text-xs text-[#8C857B]">échéance {i.deadline ? fmtDate(i.deadline) : "—"}{i.pdf_url && <> · <a href={i.pdf_url} target="_blank" rel="noreferrer" className="k-link">PDF</a></>}</span></span>
            <span className={`k-badge shrink-0 ${i.overdue ? "text-[#A84C2A] border-[#E5A992]" : ""}`}>{Number(i.remaining ?? i.amount_ttc).toFixed(2)} €</span>
          </li>
        ))}
      </ul>
      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/pennylane/unpaid.csv`} className="k-link text-xs mt-3 inline-block" data-testid="unpaid-export-csv">Exporter en CSV</a>
    </details>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const { items } = useFeed();
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, [items.length]);
  if (!d) return <p className="text-sm">Chargement…</p>;
  const open = (d.counts.nouvelle || 0) + (d.counts.a_chiffrer || 0);
  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="k-eyebrow">Tableau de bord</p><h1 className="k-h2 mt-2">{open ? `${open} demande${open > 1 ? "s" : ""} à traiter.` : "Rien d'urgent. Tout est à jour."}</h1></div>
        <div className="flex gap-2 flex-wrap">
          {["nouvelle", "a_chiffrer", "devis_envoye", "accepte"].map((k) => (
            <Link key={k} to={`/admin/dossiers?commercial=${k}`} className="k-card px-3 py-2 text-xs flex items-center gap-2 hover:border-[#C25E38]" data-testid={`dashboard-count-${k}`}>
              <span className="font-display text-xl leading-none">{d.counts[k] || 0}</span><span className="text-[#57534E]">{COMMERCIAL[k]}</span>
            </Link>
          ))}
        </div>
      </div>
      {d.failed_emails > 0 && (
        <Link to="/admin/parametres?tab=emails" className="mt-6 flex gap-3 items-center border border-[#E5A992] bg-[#FDF4EE] p-4 text-sm rounded-xl" data-testid="dashboard-failed-emails">
          <AlertTriangle size={16} className="text-[#C25E38]" /> {d.failed_emails} email(s) non envoyé(s) — les dossiers sont bien enregistrés. Voir et renvoyer.
        </Link>
      )}
      <div className="mt-8 grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <Feed />
        <div className="grid gap-6 content-start min-w-0">
          <Todo d={d} />
          <Unpaid />
          <details className="k-card p-5" data-testid="dashboard-invite">
            <summary className="cursor-pointer font-display text-xl">Inviter un client à remplir une demande</summary>
            <div className="mt-4"><InviteClient /></div>
          </details>
        </div>
      </div>
    </div>
  );
}
